import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { AppConfigService } from './app-config.service';

/**
 * FAST mode (FRU-21170): opt-in auto-complete for the consumer rates form.
 *
 * v1 behavior: when enabled, each step selects the answer the form already
 * marks as its built-in default (the `default: true` button option / slider
 * default value) and the visitor taps through, stopping at the phone step.
 *
 * "Build better answers one by one": we improve a step's answer by adding an
 * entry to `overrides` (later sourced from URL params / zip-median / inference).
 * `getAnswer(stepKey)` returns an override if present, otherwise null, which
 * tells the step to use its own built-in default.
 */
@Injectable({ providedIn: 'root' })
export class FastModeService {
  private _enabled = new BehaviorSubject<boolean>(false);
  /** Components subscribe to react to FAST mode turning on/off. */
  enabled$ = this._enabled.asObservable();

  /** Labels of questions FAST has auto-resolved — drives the on-screen green-check list. */
  private _resolved = new BehaviorSubject<string[]>([]);
  resolved$ = this._resolved.asObservable();

  /**
   * Paused = FAST is on (panel + toggle stay) but auto-advance is halted because
   * we reached a step only the user can fill (zip, contact). Resumes when the
   * user advances past it.
   */
  paused = false;

  /** Timestamp of the last auto-stop, to swallow the form's stray double-advance. */
  private _stopAt = 0;

  /** FAST will auto-advance a step only when on AND not paused. */
  get canAutoAdvance(): boolean {
    return this.enabled && !this.paused;
  }

  /** Reached a step FAST can't fill — halt the walk and note the moment. */
  pauseForUser(): void {
    this.paused = true;
    this._stopAt = Date.now();
  }

  /** User advanced past the input step — let FAST pick back up. */
  resume(): void {
    this.paused = false;
  }

  /** True briefly right after an auto-stop (used to ignore the stray double-advance). */
  justAutoStopped(): boolean {
    return (Date.now() - this._stopAt) < 700;
  }

  /** Called by a step when FAST auto-answers it. Adds a green ✓ to the list. */
  markResolved(label: string): void {
    if (!label) { return; }
    const list = this._resolved.value;
    if (list.indexOf(label) >= 0) { return; }
    this._resolved.next([...list, label]);
  }

  get enabled(): boolean {
    return this._enabled.value;
  }

  private http = inject(HttpClient);
  private appConfig = inject(AppConfigService);

  /** Per-step answer overrides. Empty in v1 (use each step's built-in default). */
  private overrides: { [stepKey: string]: any } = {};

  /** True when the entry loan type is a cash-out option (fills the cash-out amount). */
  isCashout = false;

  /** Down payment is only meaningful for purchase flows. */
  private isPurchase = false;

  /** Guard so the zip -> home-value fetch + cash-out math runs once. */
  private cashoutComputed = false;

  /** Flip FAST on (the ⚡ toggle / permission confirm). */
  enable(): void {
    this._resolved.next([]);
    this.overrides = {};
    this.isCashout = false;
    this.isPurchase = false;
    this.cashoutComputed = false;
    this.paused = false;
    this._stopAt = 0;
    this._enabled.next(true);
  }

  /** Stop FAST and hand control back ("take the wheel"); progress so far is kept. */
  disable(): void {
    this._enabled.next(false);
  }

  /**
   * The FAST answer for a step. Returns an override value if we have one,
   * otherwise null -> the step should fall back to its own default option.
   */
  getAnswer(stepKey: string): any {
    return this.overrides.hasOwnProperty(stepKey) ? this.overrides[stepKey] : null;
  }

  /** Improve a step's FAST answer (inference, zip-median, keyword mapping, etc.). */
  setOverride(stepKey: string, value: any): void {
    this.overrides[stepKey] = value;
  }

  /**
   * Basic rule engine (FRU-21170). Derives smarter-than-default answers from the
   * signals we have on the page. Rules-first; data-driven refinements (call
   * transcripts, search-term mining, zip-median home value) layer in later.
   *
   * @param signals.keyword   search term / utm_keyword (lowercased internally)
   * @param signals.loanType  LoanPurpose (refinance / purchase / ...)
   * @param signals.formType  FormType (cashout / heloc / ...)
   */
  computeOverrides(signals: { keyword?: any; loanType?: any; formType?: any }): void {
    const kw = this.toSignalString(signals.keyword).toLowerCase();

    // Cash-out path fills the cash-out amount; refinance stops at the cash-out step.
    const ft = this.toSignalString(signals.formType).toLowerCase();
    const lt = this.toSignalString(signals.loanType).toLowerCase();
    this.isPurchase = lt === 'purchase';
    this.isCashout = /cash-?out/.test(ft) || /cash-?out/.test(lt);

    // Equity-seeking loan types (Home Equity / HELOC / Cash Out / Home Improvement)
    // auto-answer the "maximum home equity?" question as Yes and continue.
    // Plain refinance/purchase get no override -> FAST stops there (the 1st cash-out question).
    const equityForms = ['heloc', 'homeequity', 'home-equity', 'cashout', 'cash-out', 'homeimprovement', 'home-improvement'];
    if (this.isCashout || equityForms.indexOf(ft) >= 0) {
      this.setOverride('maximumEquity', 'yes');
    }

    // Property type: Single Family unless the keyword names another type.
    if (/condo|condominium/.test(kw)) {
      this.setOverride('propertyType', 'Condo');
    } else if (/town\s*home|town\s*house/.test(kw)) {
      this.setOverride('propertyType', 'Town House');
    } else if (/co-?op|cooperative/.test(kw)) {
      this.setOverride('propertyType', 'Cooperative');
    } else if (/mobile|manufactured/.test(kw)) {
      this.setOverride('propertyType', 'Mobile');
    } else if (/multi[\s-]*family|multifamily|duplex|triplex|fourplex/.test(kw)) {
      this.setOverride('propertyType', 'Multi');
    }
    // else: leave unset -> the step uses its built-in default (Single Family).

    // Property use: owner-occupied unless the keyword signals investment/rental.
    if (/investment|rental|\brent\b|landlord/.test(kw)) {
      this.setOverride('propertyUse', 'investment');
    }
    // else: leave unset -> default (Primary Home).

    // Credit default is already "Good"; no override needed.
    // Cash-out dollar fields are computed once the zip is known — see
    // applyCashoutFromZip() (the slider steps trigger it).
  }

  private toSignalString(value: any): string {
    if (Array.isArray(value)) {
      return value.filter(v => v != null).join(' ');
    }

    return value == null ? '' : String(value);
  }

  /** The first dollar slider step asks this before computing. */
  shouldComputeCashout(): boolean {
    return this.enabled && !this.cashoutComputed;
  }

  /**
   * Fetch the 12-month average home value and set the dollar-field overrides per
   * Ed's basic rules: home value = avg, first mortgage balance = 65%, cash out =
   * 20% (cash-out path only — refinance stops at the cash-out step). Desired loan
   * amount is derived by the form (balance + cash out = 85%).
   *
   * The consumer's zip is collected AFTER the dollar steps, so we key off the
   * IP-derived STATE (known from the landing). zip is optional (used if we ever
   * have it earlier); the endpoint falls back state -> national.
   */
  async applyDollarDefaults(state: string, zip: string = ''): Promise<void> {
    const params: any = {};
    if (zip) { params.zip = zip; }
    if (state) { params.state = state; }
    if (this.isPurchase) { params.type = 'purchase'; }
    let res: any;

    try {
      res = await this.http.get(`${this.appConfig.adminUrl}/public_api/zip_home_value`, { params }).toPromise();
    } catch (error) {
      console.error('FastModeService: failed to fetch home value defaults', error);
      return;
    }

    const avg = Number(res && res.avgHomeValue);
    if (!avg || isNaN(avg)) { return; }
    const avgDownPayment = Number(res && res.avgDownPayment);

    this.cashoutComputed = true;
    this.setOverride('propertyValue', avg);
    this.setOverride('newHomeValue', avg);
    this.setOverride('firstMortgageBalance', Math.round(avg * 0.65));
    if (this.isPurchase && avgDownPayment > 0 && !isNaN(avgDownPayment)) {
      this.setOverride('estimatedDownPayment', avgDownPayment);
    }
    if (this.isCashout) {
      this.setOverride('cashOut', Math.round(avg * 0.20));
    }
  }
}
